/** Rich lead display for terminal assistant (search + open). */
import { esc, fmtDate, fmtAmt, fmtDateTime, leadOutstandingBalance, ticketPricingFromExtra } from './utils.js';
import { STATUS_LABELS } from './status-pipelines.js';

function profileFlightLine(kind, index, pr) {
  const tag = kind === 'stu' ? 'STU' : 'ADT';
  const genderTag = pr.gender === 'Male' ? 'M' : pr.gender === 'Female' ? 'F' : '';
  const name = pr.name?.trim();
  const label = name
    ? `${genderTag ? `${genderTag} · ` : ''}${name}`
    : `${tag} ${index + 1}${genderTag ? ` (${genderTag})` : ''}`;
  const route = `${pr.origin || '?'}→${pr.destination || '?'}`;
  const dates = pr.trip_type === 'return' && pr.return_date
    ? `${fmtDate(pr.depart_date)} / ${fmtDate(pr.return_date)}`
    : fmtDate(pr.depart_date);
  const times = [pr.depart_time, pr.arrive_time].filter(Boolean).join('–');
  const transit = pr.transit_stop
    ? ` · via ${pr.transit_stop}${pr.transit_layover ? ` (${pr.transit_layover})` : ''}`
    : '';
  const fare = (pr.cost != null || pr.profit != null)
    ? ` · ${fmtAmt((pr.cost || 0) + (pr.profit || 0))}`
    : '';
  return `${label}: ${route} ${dates}${times ? ` ${times}` : ''}${transit}${fare}`;
}

export function formatFlightDetails(lead) {
  const extra = lead.extra || {};
  const lines = [];

  if (extra.per_adt_itineraries && extra.adt_profiles?.length) {
    extra.adt_profiles.forEach((pr, i) => lines.push(profileFlightLine('adt', i, pr)));
  } else if (lead.origin || lead.destination) {
    const route = `${lead.origin || '?'}→${lead.destination || '?'}`;
    const dates = lead.trip_type === 'return' && lead.return_date
      ? `${fmtDate(lead.depart_date)} / ${fmtDate(lead.return_date)}`
      : fmtDate(lead.depart_date);
    lines.push(`${route} ${dates} (${lead.trip_type === 'return' ? 'return' : 'one way'})`);
  }

  (extra.stu_profiles || []).forEach((pr, i) => lines.push(profileFlightLine('stu', i, pr)));

  if (extra.preferences) {
    const prefs = [];
    const p = extra.preferences;
    if (p.baggage) prefs.push(`baggage:${p.baggage}`);
    if (p.seat) prefs.push(`seat:${p.seat}`);
    if (p.transit === 'direct') prefs.push('direct');
    else if (p.transit === 'multi' && p.multi_city_route) prefs.push(`multi ${p.multi_city_route}`);
    else if (p.transit === 'transit' && p.transit_via) prefs.push(`via ${p.transit_via}`);
    else if (p.transit === 'transit' || p.transit === 'ok') prefs.push('transit OK');
    else if (p.transit_via) prefs.push(`via ${p.transit_via}`);
    if (prefs.length) lines.push(`Prefs: ${prefs.join(' · ')}`);
  }

  return lines.length ? lines.join('\n') : 'No flight details recorded.';
}

export function formatLeadMoneyLines(lead) {
  const extra = lead.extra || {};
  const pricing = ticketPricingFromExtra(extra);
  const cost = lead.cost_amount ?? pricing?.cost;
  const profit = lead.profit ?? pricing?.profit;
  const total = lead.quote_amount ?? pricing?.total;
  const lines = [];
  if (cost != null) lines.push(`Cost: ${fmtAmt(cost)}`);
  if (profit != null) lines.push(`Profit: ${fmtAmt(profit)}`);
  if (total != null) lines.push(`Quote total: ${fmtAmt(total)}`);
  if (lead.sold_amount != null) lines.push(`Collected: ${fmtAmt(lead.sold_amount)}`);
  return lines;
}

export function formatDueLine(lead) {
  const due = leadOutstandingBalance(lead);
  if (!due) return null;
  const payBy = lead.extra?.remain_pay_by || lead.extra?.pay_before;
  let s = `Balance due: ${fmtAmt(due)}`;
  if (payBy) s += ` · pay by ${fmtDateTime(payBy) || payBy}`;
  return s;
}

export function formatLeadBriefRich(lead) {
  const label = STATUS_LABELS[lead.status] || lead.status;
  const typeTag = lead.type ? String(lead.type).toUpperCase() : '—';
  const route = lead.origin && lead.destination ? `${lead.origin}→${lead.destination}` : (lead.destination || lead.package_name || '—');
  const due = leadOutstandingBalance(lead);
  const duePart = due ? ` · DUE ${fmtAmt(due)}` : '';
  const entered = lead.inquiry_date ? ` · entered ${fmtDate(lead.inquiry_date)}` : (lead.created_at ? ` · entered ${fmtDate(lead.created_at.slice(0, 10))}` : '');
  return `${lead.name || '—'} · ${lead.phone || '—'} · ${typeTag} · ${label} · ${route}${duePart}${entered}`;
}

/** Compact block shown under each search hit (cost, agent, due, one flight line). */
export function formatLeadSearchBlock(lead) {
  const extra = lead.extra || {};
  const pax = extra.pax || {};
  const money = formatLeadMoneyLines(lead);
  const dueLine = formatDueLine(lead);
  const lines = [];

  if (money.length) lines.push(money.join(' · '));

  const meta = [`Agent: ${lead.agent_name || '—'}`];
  if (lead.pnr) meta.push(`PNR: ${lead.pnr}`);
  lines.push(meta.join(' · '));

  if (dueLine) lines.push(`<span class="due-text">${esc(dueLine)}</span>`);

  let flightLine = '';
  if (extra.adt_profiles?.length) {
    flightLine = profileFlightLine('adt', 0, extra.adt_profiles[0]);
    const totalPax = (pax.adt || 0) + (pax.stu || 0) + (pax.chd || 0) + (pax.inf || 0);
    if (totalPax > 1) flightLine += ` · +${totalPax - 1} more pax`;
  } else if (lead.origin || lead.destination) {
    const route = `${lead.origin || '?'}→${lead.destination || '?'}`;
    const dates = lead.trip_type === 'return' && lead.return_date
      ? `${fmtDate(lead.depart_date)} / ${fmtDate(lead.return_date)}`
      : fmtDate(lead.depart_date);
    flightLine = `${route} ${dates}`.trim();
  }
  if (flightLine) lines.push(esc(flightLine));

  return lines.join('\n');
}

export function formatLeadDetailHtml(lead, { access } = {}) {
  const extra = lead.extra || {};
  const pax = extra.pax || {};
  const due = leadOutstandingBalance(lead);
  const dueLine = formatDueLine(lead);
  const money = formatLeadMoneyLines(lead);
  const flights = formatFlightDetails(lead);

  const lines = [
    `<strong>${esc(lead.name || '—')}</strong>`,
    `Phone: ${esc(lead.phone || '—')}`,
    `Status: ${esc(STATUS_LABELS[lead.status] || lead.status)}`,
    `Agent: ${esc(lead.agent_name || '—')}`,
    `Pax: ${pax.adt ?? '?'} ADT · ${pax.stu ?? 0} STU · ${pax.chd ?? 0} CHD · ${pax.inf ?? 0} INF`,
  ];

  if (money.length) lines.push(money.join(' · '));
  if (dueLine) {
    lines.push(`<span class="due-text">${esc(dueLine)}</span>`);
  }
  if (lead.pnr) lines.push(`PNR: ${esc(lead.pnr)}`);
  if (extra.passport_no) lines.push(`Passport: ${esc(extra.passport_no)}`);
  lines.push('', esc(flights));

  if (access && !access.can_edit) {
    lines.push('', '<span class="dim-text">View only — type request edit to ask the owner.</span>');
  } else if (access?.can_edit) {
    lines.push('', '<span class="dim-text">Type edit to change details · continue stage Q&amp;A below.</span>');
  }

  return lines.join('\n');
}

export function formatPassengerSummary(data) {
  ensureTravelers(data);
  const lines = ['Passengers captured:'];
  for (let i = 0; i < data.pax.adt; i += 1) {
    const pr = data.travelers.adt[i];
    if (pr) lines.push(profileFlightLine('adt', i, pr));
  }
  for (let i = 0; i < data.pax.stu; i += 1) {
    const pr = data.travelers.stu[i];
    if (pr) lines.push(profileFlightLine('stu', i, pr));
  }
  if (data.pax.chd > 0 && data.shared_rates.chd) {
    const r = data.shared_rates.chd;
    lines.push(`CHD×${data.pax.chd}: ${fmtAmt((r.cost || 0) + (r.profit || 0))} each`);
  }
  if (data.pax.inf > 0 && data.shared_rates.inf) {
    const r = data.shared_rates.inf;
    lines.push(`INF×${data.pax.inf}: ${fmtAmt((r.cost || 0) + (r.profit || 0))} each`);
  }
  return lines.join('\n');
}

function ensureTravelers(data) {
  if (!data.travelers) data.travelers = { adt: [], stu: [] };
  if (!data.pax) data.pax = { adt: 1, stu: 0, chd: 0, inf: 0 };
}
